import { memo, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useTheme } from '@mui/material';
import useTranslation from 'canvas_states/translation/useTranslation';

import isRightToLeft from '../../utils/languages/isRightToLeft';
import type Language from '../../../../types/Language';
import mapLanguageCodeForTranslations from '../../utils/languages/mapLanguageCodeForTranslations';

export interface DocumentHeadProps {
  partnerShortName?: string;
  title?: string;
  loading?: boolean;
  description?: string;
  language: Language;
}

const PROGRESS_TITLE = '...';
const OT_SCRIPT_LOAD = 'OTScriptLoad';

const buildTemplate = (partnerName: string | undefined) => (partnerName ? `${partnerName}  |  %s` : undefined);

const buildShortchutIcon = (iconUrl: string) => {
  return { rel: 'shortcut icon', href: iconUrl, type: 'image/x-icon' };
};

const DocumentHead: React.FC<DocumentHeadProps> = memo(
  ({ title, description, partnerShortName, loading, language }: DocumentHeadProps) => {
    const theme = useTheme();

    const { t } = useTranslation().actions;

    // change OT script language
    useEffect(() => {
      const handler = () => {
        const languageCode = mapLanguageCodeForTranslations(language?.code);
        window.OneTrust?.changeLanguage(languageCode);
      };

      handler();

      window.addEventListener(OT_SCRIPT_LOAD, handler);

      return () => {
        window.removeEventListener(OT_SCRIPT_LOAD, handler);
      };
    }, [language?.code]);

    return (
      <Helmet
        htmlAttributes={{
          lang: language.code,
          dir: isRightToLeft(language) ? 'rtl' : 'ltr',
        }}
        titleTemplate={loading ? PROGRESS_TITLE : buildTemplate(partnerShortName)}
        title={title ?? t('default.page.title')}
        link={[buildShortchutIcon(theme.partner.faviconUrl)]}
      >
        <meta name="description" content={description ?? t('default.page.description')} />
      </Helmet>
    );
  },
);

export default DocumentHead;
